import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, GREEN, mono } from '../bfsi/bfsiShared';

export function SummaryCards({ summary }) {
  const entries = Object.entries(summary || {}).filter(([, v]) => typeof v !== 'object');
  return (
    <div className="flex flex-wrap gap-3 mb-5" data-testid="report-summary-cards">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-lg border bg-white px-4 py-3 min-w-[120px]" style={{ borderColor: '#E5E3DC' }}>
          <div className="text-xl font-bold" style={{ color: '#1B1D1C', ...mono }}>{v === null ? '—' : String(v)}</div>
          <div className="text-xs" style={{ color: '#9a9e96' }}>{k.replace(/_/g, ' ')}</div>
        </div>
      ))}
    </div>
  );
}

export function ReportTable({ columns, rows }) {
  const [limit, setLimit] = useState(100);
  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm" data-testid="report-table">
        <thead>
          <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
            {columns.map(c => <th key={c.key} className="px-3 py-2.5">{c.label}{c.unit ? ` (${c.unit})` : ''}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-3 py-6 text-center" style={{ color: '#9a9e96' }}>No rows for these filters</td></tr>}
          {rows.slice(0, limit).map((r, i) => (
            <tr key={r.id || columns.map(c => String(r[c.key] ?? '')).join('|') + i} className="border-t" style={{ borderColor: '#F0EEE8' }}>
              {columns.map(c => {
                const v = r[c.key];
                return <td key={c.key} className="px-3 py-2" style={typeof v === 'number' ? mono : undefined}>
                  {v === null || v === undefined ? '—' : v === true ? '✓' : v === false ? '' : String(v)}
                </td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > limit && (
        <button className="w-full py-2 text-sm hover:underline" style={{ color: GREEN }}
          onClick={() => setLimit(limit + 200)} data-testid="report-show-more">
          Show more ({rows.length - limit} hidden)
        </button>
      )}
    </Card>
  );
}

export function ReportChart({ series }) {
  if (!series || !series.length || !series[0].points?.length) return null;
  const data = series[0].points.map((p, i) => {
    const row = { t: new Date(p.t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) };
    series.forEach(s => { row[s.name] = s.points[i]?.v; });
    return row;
  });
  return (
    <Card className="mb-5" data-testid="report-chart">
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data}>
          <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={40} />
          <YAxis tick={{ fontSize: 10 }} width={40} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Line key={s.name} type="monotone" dataKey={s.name} dot={false}
              stroke={i === 0 ? GREEN : '#E4572E'} strokeWidth={1.5} connectNulls={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export function GapsNote({ gaps, locationsHidden }) {
  if ((!gaps || !gaps.length) && !locationsHidden) return null;
  return (
    <div className="rounded-lg border px-4 py-2.5 mb-5 text-xs" data-testid="report-gaps-note"
      style={{ background: '#FDF3E0', borderColor: '#EBD9AE', color: '#9a6b12' }}>
      {gaps?.length > 0 && <div>{gaps.length} offline/unmonitored gap(s) — shown as gaps, never interpolated.</div>}
      {locationsHidden && <div>Locations are hidden for you (BFSI policy — needs a customer-approved grant).</div>}
    </div>
  );
}
